import { effect } from "@angular/core";
import { EmptyFeatureResult, getState, patchState, SignalStoreFeature, SignalStoreFeatureResult } from "@ngrx/signals";
import type { ISignalStorageConfig } from "../../models/interfaces/signal-storage.interface";

function getFromStorage<State extends SignalStoreFeatureResult>(key: string, storage: Storage, cfg: ISignalStorageConfig<State["state"]>): string | null {
  try {
    return storage.getItem(key);
  } catch (e) {
    cfg.error(e);
    return null;
  }
}

export function withStorage<State extends SignalStoreFeatureResult>(
  key: string,
  storage: Storage,
  config?: Partial<ISignalStorageConfig<State["state"]>>
): SignalStoreFeature<State, EmptyFeatureResult> {
  const defaultConfig: ISignalStorageConfig<State["state"]> = {
    saveExcludeKeys: [],
    loadExcludeKeys: [],
    serialize: (state: State["state"]) => JSON.stringify(state),
    deserialize: (state: string) => JSON.parse(state) as State["state"],
    saveIf: true,
    error: (error: unknown) => console.error(error),
  };

  const cfg = { ...defaultConfig, ...config };

  const item = getFromStorage(key, storage, cfg);
  const storageState: State["state"] | null = item ? cfg.deserialize(item) : null;

  let hydrated = false;
  //TODO implement control when to load from
  return (store) => {
    if (storageState !== null && !hydrated) {
      const stateSignals = store["stateSignals"];
      const stateSignalKeys = Object.keys(stateSignals);
      const state = stateSignalKeys.reduce((state: State["state"], key: string) => {
        if (!cfg.loadExcludeKeys.includes(key as keyof State["state"])) {
          const value = storageState[key as keyof State["state"]];
          return value ? { ...state, [key]: value } : state;
        }
        return state;
      }, getState(store));

      patchState(store, state);
      hydrated = true;
    }

    effect(() => {
      const state = structuredClone(getState(store));

      try {
        const shouldSave = typeof cfg.saveIf === "function" ? cfg.saveIf(state) : cfg.saveIf;
        if (shouldSave) {
          cfg.saveExcludeKeys.forEach((key: keyof State["state"]) => {
            delete (state as Record<string, unknown>)[key as string];
          });
          storage.setItem(key, cfg.serialize(state));
        }
      } catch (e) {
        cfg.error(e);
      }
    });

    return store;
  };
}
